import Papa from "papaparse";
import * as XLSX from "xlsx";

// Forgiving alias map for common header variants admins produce in the wild
// (and that even our own legacy sample CSVs ship with). Applied AFTER
// lowercase+trim. Conservative — only known aliases, so genuine typos still
// surface as missing-field errors instead of being silently mapped.
//
// Two patterns dominate:
//  - Hyphen instead of underscore in the Arabic-prefixed columns: ar-name,
//    ar-category, ar-sub_category, ar-type. Spreadsheet apps often auto-format
//    these on paste.
//  - Plural taxonomy column names: Brands, Categories, Sub_Categories, etc.
//    People pluralize because the column may list multiple values per row.
const HEADER_ALIASES = {
  // Hyphen → underscore variants
  "ar-name": "ar_name",
  "ar-description": "ar_description",
  "ar-category": "ar_category",
  "ar-sub_category": "ar_sub_category",
  "ar-sub-category": "ar_sub_category",
  "ar-type": "ar_type",
  "ar-attributes": "ar_attributes",
  // Plural taxonomy field names → singular
  brands: "brand",
  categories: "category",
  sub_categories: "sub_category",
  types: "type",
  colors: "color",
  sizes: "size",
  occasions: "occasion",
  // Legacy / synonym field names
  stock: "quantity",
  qty: "quantity",
  image: "main_image",
};

function normalizeHeader(rawHeader) {
  const lowered = String(rawHeader || "").trim().toLowerCase();
  return HEADER_ALIASES[lowered] || lowered;
}

// Parses a user-supplied product file into the row shape the bulk-stage endpoint
// expects: an array of plain objects keyed by lowercased, trimmed, alias-normalized
// header names (the backend's required-field check is case-sensitive on lowercase).
//
// CSV files are parsed with PapaParse; .xlsx/.xls with SheetJS. Both branches
// produce the same shape so the upload pipeline downstream doesn't care which
// format the user chose. We deliberately do NOT enable PapaParse `dynamicTyping`
// — the server coerces numerics, and dynamicTyping converts "true"/"false" cells
// to JS booleans, which the server's float() coercion would then reject.
export async function parseProductsFile(file) {
  const lowerName = file.name.toLowerCase();

  if (lowerName.endsWith(".csv")) {
    return parseCsv(file);
  }
  if (lowerName.endsWith(".xlsx") || lowerName.endsWith(".xls")) {
    return parseExcel(file);
  }
  throw new Error(`Unsupported file extension: ${file.name}`);
}

function parseCsv(file) {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: normalizeHeader,
      complete: (result) => {
        if (result.errors && result.errors.length > 0) {
          // PapaParse reports per-row recoverable errors here; keep the parse
          // going but expose them so callers can warn.
          console.warn("[parseProductsFile] CSV parse warnings:", result.errors);
        }
        resolve({ rows: result.data, headers: result.meta?.fields || [] });
      },
      error: reject,
    });
  });
}

async function parseExcel(file) {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: "array" });
  const firstSheetName = wb.SheetNames[0];
  if (!firstSheetName) {
    return { rows: [], headers: [] };
  }
  const sheet = wb.Sheets[firstSheetName];
  // defval:"" means missing cells become empty strings (matches PapaParse's
  // skipEmptyLines + missing-cell behavior). raw:false would force string
  // coercion — we leave it default so dates/numbers come through as their
  // native types and let the server coerce.
  const rawRows = XLSX.utils.sheet_to_json(sheet, { defval: "" });
  const rows = rawRows.map((row) =>
    Object.fromEntries(
      Object.entries(row).map(([k, v]) => [normalizeHeader(k), v])
    )
  );
  const headers = rows.length > 0 ? Object.keys(rows[0]) : [];
  return { rows, headers };
}
